import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import { Card, Button, Input, Select, Badge, Modal } from '../components/ui';
import { 
  Plus, 
  Bell, 
  Droplets, 
  Sun, 
  Calendar,
  Clock,
  Trash2,
  Edit,
  Loader2,
  ToggleLeft,
  ToggleRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Reminders = () => {
  const { t } = useTranslation();
  const [reminders, setReminders] = useState([]);
  const [tattoos, setTattoos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedReminder, setSelectedReminder] = useState(null);

  const [formData, setFormData] = useState({
    tattoo_id: '',
    type: 'moisturize',
    title: '',
    frequency: 'daily',
    time: '10:00',
  });

  const reminderTypes = [
    { value: 'moisturize', label: 'Nemlendirme', icon: Droplets, color: 'text-blue-400' },
    { value: 'sunscreen', label: 'Güneş Kremi', icon: Sun, color: 'text-yellow-400' },
    { value: 'checkup', label: 'Kontrol', icon: Calendar, color: 'text-green-400' },
    { value: 'custom', label: 'Özel', icon: Bell, color: 'text-purple-400' },
  ];

  const frequencies = [
    { value: 'daily', label: 'Her Gün' },
    { value: 'twice_daily', label: 'Günde 2 Kez' },
    { value: 'weekly', label: 'Haftalık' },
    { value: 'custom', label: 'Özel' },
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [remindersRes, tattoosRes] = await Promise.all([
        fetch(`${API_URL}/api/reminders`, { credentials: 'include' }),
        fetch(`${API_URL}/api/tattoos`, { credentials: 'include' })
      ]);

      if (remindersRes.ok) {
        setReminders(await remindersRes.json());
      }
      if (tattoosRes.ok) {
        setTattoos(await tattoosRes.json());
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const method = selectedReminder ? 'PUT' : 'POST';
      const url = selectedReminder 
        ? `${API_URL}/api/reminders/${selectedReminder.reminder_id}`
        : `${API_URL}/api/reminders`;

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        fetchData();
        setShowModal(false);
        resetForm();
      }
    } catch (error) {
      console.error('Failed to save reminder:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (reminderId) => {
    if (!window.confirm('Bu hatırlatıcıyı silmek istediğinizden emin misiniz?')) return;

    try {
      const response = await fetch(`${API_URL}/api/reminders/${reminderId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        fetchData();
      }
    } catch (error) {
      console.error('Failed to delete reminder:', error);
    }
  };

  const toggleReminder = async (reminder) => {
    try {
      await fetch(`${API_URL}/api/reminders/${reminder.reminder_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ enabled: !reminder.enabled }),
      });
      fetchData();
    } catch (error) {
      console.error('Failed to toggle reminder:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      tattoo_id: '',
      type: 'moisturize',
      title: '',
      frequency: 'daily',
      time: '10:00',
    });
    setSelectedReminder(null);
  };

  const openEditModal = (reminder) => {
    setSelectedReminder(reminder);
    setFormData({
      tattoo_id: reminder.tattoo_id || '',
      type: reminder.type || 'moisturize',
      title: reminder.title || '',
      frequency: reminder.frequency || 'daily',
      time: reminder.time || '10:00',
    });
    setShowModal(true);
  };

  const getTypeInfo = (type) => {
    return reminderTypes.find(t => t.value === type) || reminderTypes[3];
  };

  const getTattooName = (tattooId) => {
    const tattoo = tattoos.find(t => t.tattoo_id === tattooId);
    return tattoo?.name || 'Genel';
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl mb-1" data-testid="reminders-title">
              Bakım <span className="text-primary">Hatırlatıcıları</span>
            </h1>
            <p className="text-muted-foreground">
              {reminders.filter(r => r.enabled).length} aktif hatırlatıcı
            </p>
          </div>
          <Button onClick={() => { resetForm(); setShowModal(true); }} data-testid="add-reminder-btn">
            <Plus className="w-4 h-4 mr-2" />
            Yeni Hatırlatıcı
          </Button>
        </div>

        {/* Reminders List */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-10 h-10 text-primary animate-spin" />
          </div>
        ) : reminders.length > 0 ? (
          <div className="space-y-4">
            {reminders.map((reminder, index) => {
              const typeInfo = getTypeInfo(reminder.type);
              const Icon = typeInfo.icon;

              return (
                <motion.div
                  key={reminder.reminder_id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card 
                    className={`p-4 ${!reminder.enabled ? 'opacity-50' : ''}`}
                    data-testid={`reminder-card-${index}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-sm bg-muted flex items-center justify-center`}>
                        <Icon className={`w-6 h-6 ${typeInfo.color}`} />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-medium truncate">{reminder.title}</h3>
                          {!reminder.enabled && (
                            <Badge variant="secondary">Devre Dışı</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {reminder.time}
                          </span>
                          <span>{frequencies.find(f => f.value === reminder.frequency)?.label}</span>
                          {reminder.tattoo_id && (
                            <span className="truncate">• {getTattooName(reminder.tattoo_id)}</span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => toggleReminder(reminder)}
                          data-testid={`toggle-reminder-${index}`}
                        >
                          {reminder.enabled ? (
                            <ToggleRight className="w-6 h-6 text-primary" />
                          ) : (
                            <ToggleLeft className="w-6 h-6 text-muted-foreground" />
                          )}
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => openEditModal(reminder)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="text-destructive hover:text-destructive"
                          onClick={() => handleDelete(reminder.reminder_id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        ) : (
          <Card className="p-12 text-center">
            <Bell className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-heading text-xl mb-2">Henüz Hatırlatıcı Yok</h3>
            <p className="text-muted-foreground mb-6">
              Bakım rutininizi asla unutmamak için hatırlatıcılar ekleyin.
            </p>
            <Button onClick={() => setShowModal(true)}>
              <Plus className="w-4 h-4 mr-2" />
              İlk Hatırlatıcınızı Ekleyin
            </Button>
          </Card>
        )}

        {/* Suggested Reminders */}
        {reminders.length < 3 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mt-8"
          >
            <h2 className="font-heading text-xl mb-4">Önerilen Hatırlatıcılar</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { type: 'moisturize', title: 'Sabah Nemlendirme', time: '09:00' },
                { type: 'sunscreen', title: 'Güneş Kremi', time: '11:00' },
                { type: 'moisturize', title: 'Akşam Nemlendirme', time: '21:00' },
              ].filter(s => !reminders.some(r => r.title === s.title)).slice(0, 2).map((suggestion, index) => {
                const typeInfo = getTypeInfo(suggestion.type);
                const Icon = typeInfo.icon;

                return (
                  <Card key={index} className="p-4 border-dashed">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-sm bg-muted/50 flex items-center justify-center">
                        <Icon className={`w-5 h-5 ${typeInfo.color}`} />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">{suggestion.title}</p>
                        <p className="text-sm text-muted-foreground">{suggestion.time}</p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setFormData({
                            ...formData,
                            type: suggestion.type,
                            title: suggestion.title,
                            time: suggestion.time,
                          });
                          setShowModal(true);
                        }}
                      >
                        Ekle
                      </Button>
                    </div>
                  </Card>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Add/Edit Modal */}
        <AnimatePresence>
          {showModal && (
            <Modal
              isOpen={showModal}
              onClose={() => { setShowModal(false); resetForm(); }}
              title={selectedReminder ? 'Hatırlatıcıyı Düzenle' : 'Yeni Hatırlatıcı'}
            >
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Tür</label>
                  <div className="grid grid-cols-2 gap-2">
                    {reminderTypes.map((type) => {
                      const Icon = type.icon;
                      return (
                        <button
                          key={type.value}
                          type="button"
                          onClick={() => setFormData({...formData, type: type.value})}
                          className={`p-3 rounded-sm border flex items-center gap-2 transition-colors ${
                            formData.type === type.value
                              ? 'border-primary bg-primary/10'
                              : 'border-border hover:border-primary/50'
                          }`}
                        >
                          <Icon className={`w-5 h-5 ${type.color}`} />
                          <span className="text-sm">{type.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Başlık *</label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    placeholder="Örn: Sabah Nemlendirme"
                    required
                    data-testid="reminder-title-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Dövme (Opsiyonel)</label>
                  <Select
                    value={formData.tattoo_id}
                    onChange={(e) => setFormData({...formData, tattoo_id: e.target.value})}
                    data-testid="reminder-tattoo-select"
                  >
                    <option value="">Genel Hatırlatıcı</option>
                    {tattoos.map(tattoo => (
                      <option key={tattoo.tattoo_id} value={tattoo.tattoo_id}>
                        {tattoo.name}
                      </option>
                    ))}
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Sıklık</label>
                    <Select
                      value={formData.frequency}
                      onChange={(e) => setFormData({...formData, frequency: e.target.value})}
                      data-testid="reminder-frequency-select"
                    >
                      {frequencies.map(freq => (
                        <option key={freq.value} value={freq.value}>{freq.label}</option>
                      ))}
                    </Select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Saat</label>
                    <Input
                      type="time"
                      value={formData.time}
                      onChange={(e) => setFormData({...formData, time: e.target.value})}
                      data-testid="reminder-time-input"
                    />
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button 
                    type="button" 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => { setShowModal(false); resetForm(); }}
                  >
                    İptal
                  </Button>
                  <Button type="submit" className="flex-1" disabled={loading} data-testid="save-reminder-btn">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Kaydet'}
                  </Button>
                </div>
              </form>
            </Modal>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
};

export default Reminders;
